import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

export default function CustomCursor() {
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    // Only enable on desktop with fine mouse pointer, and respect prefers-reduced-motion
    const isFinePointer = window.matchMedia("(pointer: fine)").matches;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!isFinePointer || prefersReducedMotion) {
      return;
    }

    setIsEnabled(true);

    const cursor = cursorRef.current;
    const follower = followerRef.current;
    if (!cursor || !follower) return;

    // High performance quickTo setters
    const xDot = gsap.quickTo(cursor, "x", { duration: 0.1, ease: "power3" });
    const yDot = gsap.quickTo(cursor, "y", { duration: 0.1, ease: "power3" });

    const xRing = gsap.quickTo(follower, "x", { duration: 0.35, ease: "power3.out" });
    const yRing = gsap.quickTo(follower, "y", { duration: 0.35, ease: "power3.out" });

    let isVisible = false;

    const handleMouseMove = (e) => {
      if (!isVisible) {
        gsap.to([cursor, follower], { opacity: 1, duration: 0.3 });
        isVisible = true;
      }
      xDot(e.clientX);
      yDot(e.clientY);
      xRing(e.clientX);
      yRing(e.clientY);
    };

    const handleMouseLeave = () => {
      gsap.to([cursor, follower], { opacity: 0, duration: 0.3 });
      isVisible = false;
    };

    const handleMouseOver = (e) => {
      const target = e.target.closest("a, button, [role='button'], input, textarea, select, [data-cursor-hover]");
      if (target) {
        setIsHovered(true);
      } else {
        setIsHovered(false);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mouseover", handleMouseOver);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseover", handleMouseOver);
    };
  }, []);

  if (!isEnabled) return null;

  return (
    <>
      {/* Central Cyan Dot */}
      <div
        ref={cursorRef}
        className="fixed top-0 left-0 w-2 h-2 rounded-full bg-[#7dd3fc] pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 opacity-0 shadow-[0_0_10px_#7dd3fc]"
      />

      {/* Outer Magnetic Glowing Ring */}
      <div
        ref={followerRef}
        className={`fixed top-0 left-0 rounded-full pointer-events-none z-[9998] -translate-x-1/2 -translate-y-1/2 opacity-0 transition-transform duration-200 border ${
          isHovered
            ? "w-12 h-12 border-[#7dd3fc] bg-[#006699]/15 shadow-[0_0_25px_rgba(125,211,252,0.4)] scale-125 backdrop-blur-[1px]"
            : "w-8 h-8 border-[#006699]/60 bg-transparent shadow-[0_0_15px_rgba(0,102,153,0.3)] scale-100"
        }`}
      />
    </>
  );
}
